import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import {
  Avatar,
  Box,
  CircularProgress,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { Hicon } from './Hicon'
import {
  clearAssistantHistory,
  fetchAssistantHistory,
  fetchOrgLlmSettings,
  importAssistantHistory,
  sendAssistantChat,
  transcribeAssistantAudio,
} from '../api/bridge'
import { useBiSource } from '../state/BiSourceContext'
import { notifyPanelsAfterRicaReply } from '../utils/biRefresh'
import { OrgLlmSettingsDialog } from './OrgLlmSettingsDialog'
import { pageDomain } from './panelInsightsDomains'

type Props = {
  onClose: () => void
  initialPrompt?: string | null
  expanded?: boolean
  onToggleExpand?: () => void
}

type ChatMessage = {
  id: string
  role: 'user' | 'assistant' | 'system'
  text: string
  at?: string
  channel?: string
}

// Mapa rota → domínio partilhado com a gaveta «Insights» (panelInsightsDomains.ts).

const STORAGE_KEY = 'fiesta-bi-rica-chat-v1'
const MODEL_KEY = 'fiesta-bi-rica-model'
const SMOKE_USER_STEMS = [
  'qual nossa venda hoje?',
  'onde vendemos?',
  'qual foi o frete por canal nos últimos 7 dias',
  'explique a diferença entre esta semana e os últimos 7 dias',
  'qual é a cobertura de notas fiscais desta fonte',
  'sem eu ensinar campos ou coleções',
  'quais foram os produtos mais vendidos por quantidade nos últimos 7 dias',
  'quais canais tiveram margens com prejuízo nos últimos 7 dias',
  'quantas notas fiscais acima de r$ 1.000',
  'na fonte fiesta, quantos pedidos válidos com receita acima de r$ 500',
]
const FALLBACK_CHAT_MODELS = [
  { id: 'claude-sonnet-4-6', label: 'Claude Sonnet', hint: 'Anthropic' },
] as const
const DEFAULT_CHAT_MODEL = FALLBACK_CHAT_MODELS[0].id

function loadChatModel(): string {
  try {
    const raw = localStorage.getItem(MODEL_KEY)
    if (raw) return raw
  } catch {
    /* ignore */
  }
  return DEFAULT_CHAT_MODEL
}

function uid() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

function isSmokeUserText(text: string) {
  const normalized = text.trim().toLowerCase().replace(/\s+/g, ' ')
  return SMOKE_USER_STEMS.some(
    (stem) => normalized === stem || normalized.startsWith(stem),
  )
}

function stripSmokeTurns(msgs: ChatMessage[]): ChatMessage[] {
  const kept: ChatMessage[] = []
  let skipReply = false
  for (const item of msgs) {
    if (skipReply) {
      skipReply = false
      if (item.role === 'assistant' || item.role === 'system') continue
    }
    const smoke =
      item.channel === 'smoke' ||
      (item.role === 'user' && isSmokeUserText(item.text || ''))
    if (smoke) {
      skipReply = item.role === 'user'
      continue
    }
    kept.push(item)
  }
  return kept
}

function loadPersisted(): { messages: ChatMessage[]; sessionId: string | null } {
  try {
    const raw =
      localStorage.getItem(STORAGE_KEY) || localStorage.getItem('fiesta-bi-hermes-chat-v1')
    if (!raw) return { messages: [], sessionId: null }
    const data = JSON.parse(raw) as { messages?: ChatMessage[]; sessionId?: string | null }
    const msgs = Array.isArray(data.messages) ? data.messages : []
    const cleaned = stripSmokeTurns(
      msgs.filter(
        (m) =>
          m.id !== 'welcome' &&
          !(
            m.role === 'system' &&
            /(Hermes|Rica|Fiesta) por aqui|Conversa nova/.test(m.text)
          ),
      ),
    )
    return { messages: cleaned.slice(-80), sessionId: data.sessionId || null }
  } catch {
    return { messages: [], sessionId: null }
  }
}

function pickRecorderMime(): string | undefined {
  if (typeof MediaRecorder === 'undefined') return undefined
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/mp4',
  ]
  return candidates.find((m) => MediaRecorder.isTypeSupported(m))
}

/** Renderiza texto com links markdown e URLs longas quebráveis. */
function MessageBody({ text, muted }: { text: string; muted?: boolean }) {
  const parts: Array<{ type: 'text' | 'link'; value: string; href?: string }> = []
  const re = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s]+)/g
  let last = 0
  let m: RegExpExecArray | null
  while ((m = re.exec(text))) {
    if (m.index > last) parts.push({ type: 'text', value: text.slice(last, m.index) })
    if (m[1] && m[2]) {
      parts.push({ type: 'link', value: m[1], href: m[2] })
    } else if (m[3]) {
      const href = m[3]
      const label = href.length > 48 ? `${href.slice(0, 40)}…` : href
      parts.push({ type: 'link', value: label, href })
    }
    last = m.index + m[0].length
  }
  if (last < text.length) parts.push({ type: 'text', value: text.slice(last) })

  return (
    <Typography
      variant={muted ? 'caption' : 'body2'}
      color={muted ? 'text.secondary' : 'inherit'}
      component="div"
      sx={{
        whiteSpace: 'pre-wrap',
        overflowWrap: 'anywhere',
        wordBreak: 'break-word',
        maxWidth: '100%',
      }}
    >
      {parts.map((p, i) =>
        p.type === 'link' && p.href ? (
          <Link
            key={i}
            href={p.href}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ overflowWrap: 'anywhere', wordBreak: 'break-all' }}
          >
            {p.value}
          </Link>
        ) : (
          <span key={i}>{p.value}</span>
        ),
      )}
    </Typography>
  )
}

function messageClock(id: string, at?: string): string | null {
  const parsed = at ? Date.parse(at) : Number.NaN
  const n = Number.isFinite(parsed) ? parsed : Number(String(id).split('-')[0])
  if (!Number.isFinite(n) || n < 1_000_000_000_000) return null
  return new Date(n).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Sao_Paulo',
  })
}

function MicGlyph() {
  return (
    <Box component="svg" viewBox="0 0 24 24" sx={{ width: 22, height: 22 }} aria-hidden>
      <path
        fill="currentColor"
        d="M12 14a3 3 0 0 0 3-3V6a3 3 0 1 0-6 0v5a3 3 0 0 0 3 3Zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.92V21h2v-3.08A7 7 0 0 0 19 11h-2Z"
      />
    </Box>
  )
}

function SendGlyph() {
  return (
    <Box component="svg" viewBox="0 0 24 24" sx={{ width: 22, height: 22 }} aria-hidden>
      <path fill="currentColor" d="M3.4 20.6 21 12 3.4 3.4 3 10.2l12.6 1.8L3 13.8z" />
    </Box>
  )
}

function MoreGlyph() {
  return (
    <Box component="svg" viewBox="0 0 24 24" sx={{ width: 22, height: 22 }} aria-hidden>
      <circle cx="12" cy="5" r="1.7" fill="currentColor" />
      <circle cx="12" cy="12" r="1.7" fill="currentColor" />
      <circle cx="12" cy="19" r="1.7" fill="currentColor" />
    </Box>
  )
}

function JumpDownGlyph() {
  return (
    <Box component="svg" viewBox="0 0 24 24" sx={{ width: 26, height: 26 }} aria-hidden>
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 5v12m0 0-5-5m5 5 5-5"
      />
    </Box>
  )
}

function PlayGlyph() {
  return (
    <Box component="svg" viewBox="0 0 24 24" sx={{ width: 20, height: 20 }} aria-hidden>
      <path fill="currentColor" d="M8 5.2v13.6L19 12 8 5.2Z" />
    </Box>
  )
}

function StopGlyph() {
  return (
    <Box component="svg" viewBox="0 0 24 24" sx={{ width: 18, height: 18 }} aria-hidden>
      <rect x="6" y="6" width="12" height="12" rx="2" fill="currentColor" />
    </Box>
  )
}

function formatRecClock(ms: number) {
  const cs = Math.floor(ms / 10) % 100
  const sec = Math.floor(ms / 1000) % 60
  const min = Math.floor(ms / 60000)
  return `${min}:${String(sec).padStart(2, '0')},${String(cs).padStart(2, '0')}`
}

function RecWaveform({ stream, paused }: { stream: MediaStream | null; paused: boolean }) {
  const bars = 28
  const [levels, setLevels] = useState<number[]>(() => Array.from({ length: bars }, () => 0.18))
  useEffect(() => {
    if (!stream || paused) return
    const AudioCtx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (!AudioCtx) return
    const ctx = new AudioCtx()
    const src = ctx.createMediaStreamSource(stream)
    const analyser = ctx.createAnalyser()
    analyser.fftSize = 64
    src.connect(analyser)
    const data = new Uint8Array(analyser.frequencyBinCount)
    let raf = 0
    const tick = () => {
      analyser.getByteFrequencyData(data)
      const step = Math.max(1, Math.floor(data.length / bars))
      setLevels(Array.from({ length: bars }, (_, i) => Math.max(0.12, data[i * step] / 255)))
      raf = requestAnimationFrame(tick)
    }
    void ctx.resume()
    tick()
    return () => {
      cancelAnimationFrame(raf)
      void ctx.close()
    }
  }, [stream, paused])
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px', flex: 1, minWidth: 0, height: 28 }}>
      {levels.map((level, i) => (
        <Box
          key={i}
          sx={{
            flex: 1,
            maxWidth: 4,
            height: `${10 + level * 16}px`,
            borderRadius: 999,
            bgcolor: 'primary.main',
          }}
        />
      ))}
    </Box>
  )
}

const LIST_SCROLL_SX = {
  scrollbarWidth: 'thin',
  scrollbarColor: 'rgba(15, 23, 42, 0.28) transparent',
  '&::-webkit-scrollbar': { width: 3 },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(15, 23, 42, 0.32)',
    borderRadius: 999,
  },
} as const

/** Chat Fiesta IA — mesmo cérebro do Telegram (via Agent Gateway). */
export function AssistantDrawer({ onClose, initialPrompt, expanded, onToggleExpand }: Props) {
  const { pathname } = useLocation()
  const { activeSource, activeSourceId, isFiestaActive } = useBiSource()
  const insightDomain = useMemo(() => pageDomain(pathname)?.dominio, [pathname])
  const persisted = useRef(loadPersisted())
  const skipHydrateRestore = useRef(false)
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(false)
  const [recPhase, setRecPhase] = useState<'idle' | 'recording' | 'review' | 'playing'>('idle')
  const [recMs, setRecMs] = useState(0)
  const [recStream, setRecStream] = useState<MediaStream | null>(null)
  const [transcribing, setTranscribing] = useState(false)
  const [sessionId, setSessionId] = useState<string | null>(persisted.current.sessionId)
  const [chatModel, setChatModel] = useState(loadChatModel)
  const [chatModels, setChatModels] = useState<
    Array<{ id: string; label: string; hint?: string }>
  >([...FALLBACK_CHAT_MODELS])
  const [messages, setMessages] = useState<ChatMessage[]>(persisted.current.messages)
  const [menuEl, setMenuEl] = useState<HTMLElement | null>(null)
  const [llmSettingsOpen, setLlmSettingsOpen] = useState(false)
  const [showJump, setShowJump] = useState(false)
  const bottomRef = useRef<HTMLDivElement | null>(null)
  const listRef = useRef<HTMLDivElement | null>(null)
  const stickToBottom = useRef(true)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const mediaStreamRef = useRef<MediaStream | null>(null)
  const chunksRef = useRef<BlobPart[]>([])
  const recIntentRef = useRef<'discard' | 'review' | 'send'>('send')
  const recTickAtRef = useRef(0)
  const recAccumRef = useRef(0)
  const recBlobRef = useRef<Blob | null>(null)
  const recMimeRef = useRef('audio/webm')
  const recUrlRef = useRef<string | null>(null)
  const previewAudioRef = useRef<HTMLAudioElement | null>(null)

  const recOpen = recPhase !== 'idle'
  const voiceBusy = busy || recOpen || transcribing
  const micSupported =
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof MediaRecorder !== 'undefined'

  function syncJumpVisibility() {
    const el = listRef.current
    if (!el) return
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight
    const near = dist < 80
    stickToBottom.current = near
    setShowJump(!near)
  }

  function jumpToBottom() {
    stickToBottom.current = true
    setShowJump(false)
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    if (stickToBottom.current) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages, busy, recPhase, transcribing])

  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ messages: messages.slice(-80), sessionId }),
      )
    } catch {
      /* quota / private mode */
    }
  }, [messages, sessionId])

  useEffect(() => {
    try {
      localStorage.setItem(MODEL_KEY, chatModel)
    } catch {
      /* quota / private mode */
    }
  }, [chatModel])

  useEffect(() => {
    let cancelled = false
    fetchOrgLlmSettings({ sourceId: activeSourceId || 'fiesta' })
      .then((data) => {
        if (cancelled) return
        const allowed = new Set(data.allowed_models || [])
        const catalog = (data.product_models || []).filter(
          (m) =>
            (!m.provider || m.provider === data.provider) &&
            (allowed.size === 0 || allowed.has(m.id)),
        )
        const list =
          catalog.length > 0
            ? catalog.map((m) => ({
                id: m.id,
                label: m.label,
                hint: m.hint || '',
              }))
            : [...FALLBACK_CHAT_MODELS]
        setChatModels(list)
        setChatModel((prev) => {
          if (list.some((m) => m.id === prev)) return prev
          if (data.default_model && list.some((m) => m.id === data.default_model)) {
            return data.default_model
          }
          return list[0]?.id || DEFAULT_CHAT_MODEL
        })
      })
      .catch(() => {
        if (!cancelled) setChatModels([...FALLBACK_CHAT_MODELS])
      })
    return () => {
      cancelled = true
    }
  }, [activeSourceId, llmSettingsOpen])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        if (skipHydrateRestore.current) return
        let remote = await fetchAssistantHistory()
        if (skipHydrateRestore.current || cancelled) return
        const local = persisted.current
        const localChat = local.messages.filter((m) => m.id !== 'welcome')
        const serverWiped = Boolean(remote.wiped_at) && !(remote.messages || []).length
        const serverHasChat = (remote.messages || []).some(
          (m) => m.role === 'user' || m.role === 'assistant' || m.role === 'system',
        )
        if (serverWiped) {
          try {
            localStorage.removeItem(STORAGE_KEY)
            localStorage.removeItem('fiesta-bi-hermes-chat-v1')
          } catch {
            /* quota / private mode */
          }
          persisted.current = { messages: [], sessionId: null }
        }
        // Servidor é a caixa partilhada. Só enviar o cache local se a caixa
        // ainda estiver vazia (migração). Senão o browser ressuscita testes.
        if (
          !serverWiped &&
          !serverHasChat &&
          localChat.some((m) => m.role === 'user' || m.role === 'assistant')
        ) {
          remote = await importAssistantHistory(localChat, local.sessionId)
        }
        if (skipHydrateRestore.current || cancelled) return
        const incoming = stripSmokeTurns(
          (remote.messages || []).filter((m) => m.text),
        )
        if (incoming.length) {
          setMessages(incoming)
        } else {
          setMessages([])
          setSessionId(null)
        }
        if (remote.session_id) setSessionId(remote.session_id)
      } catch {
        /* fica o cache local até o servidor responder */
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (initialPrompt) setInput(initialPrompt)
  }, [initialPrompt])

  useEffect(() => {
    if (recPhase !== 'recording') return
    recTickAtRef.current = Date.now()
    const id = window.setInterval(() => {
      setRecMs(recAccumRef.current + (Date.now() - recTickAtRef.current))
    }, 50)
    return () => window.clearInterval(id)
  }, [recPhase])

  useEffect(() => {
    return () => {
      recIntentRef.current = 'discard'
      previewAudioRef.current?.pause()
      if (recUrlRef.current) URL.revokeObjectURL(recUrlRef.current)
      try {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
          mediaRecorderRef.current.stop()
        }
      } catch {
        /* ignore */
      }
      mediaStreamRef.current?.getTracks().forEach((t) => t.stop())
    }
  }, [])

  async function clearChat() {
    skipHydrateRestore.current = true
    persisted.current = { messages: [], sessionId: null }
    setMessages([])
    setSessionId(null)
    try {
      localStorage.removeItem(STORAGE_KEY)
    } catch {
      /* ignore */
    }
    try {
      await clearAssistantHistory()
    } catch {
      /* caixa local já limpa */
    }
  }

  async function ask(text: string) {
    const message = text.trim()
    if (!message || busy) return

    setInput('')
    stickToBottom.current = true
    setShowJump(false)
    setMessages((prev) => [...prev, { id: uid(), role: 'user', text: message }])
    setBusy(true)
    try {
      const context = {
        page: pathname,
        insight_dominio: insightDomain,
        source_id: activeSourceId || 'fiesta',
        source_name: activeSource?.name || (isFiestaActive ? 'Fiesta' : activeSourceId),
        source_db_name:
          activeSource?.db_name || (isFiestaActive ? 'ecommerce_financial' : undefined),
      }
      const res = await sendAssistantChat(message, sessionId, context, chatModel)
      if (res.session_id) setSessionId(res.session_id)
      if (res.ok && res.reply) {
        setMessages((prev) => [...prev, { id: uid(), role: 'assistant', text: res.reply! }])
        notifyPanelsAfterRicaReply(message)
        window.setTimeout(() => notifyPanelsAfterRicaReply(message), 1500)
      } else {
        const err =
          res.message ||
          res.error ||
          'Não consegui obter resposta da Fiesta IA. Tente de novo em instantes.'
        setMessages((prev) => [...prev, { id: uid(), role: 'system', text: err }])
      }
    } catch (e) {
      const err =
        e instanceof Error && e.name === 'AbortError'
          ? 'Tempo esgotado — a Fiesta IA ainda pode estar a processar. Tente uma pergunta mais curta.'
          : e instanceof Error
            ? e.message
            : 'Falha de rede ao falar com a Fiesta IA.'
      setMessages((prev) => [...prev, { id: uid(), role: 'system', text: err }])
    } finally {
      setBusy(false)
    }
  }

  async function handleAudioBlob(blob: Blob, mime: string) {
    setTranscribing(true)
    setMessages((prev) => [
      ...prev,
      { id: uid(), role: 'system', text: 'A transcrever áudio…' },
    ])
    try {
      const res = await transcribeAssistantAudio(blob, mime)
      setMessages((prev) => {
        const next = [...prev]
        const last = next[next.length - 1]
        if (last?.role === 'system' && last.text.startsWith('A transcrever')) next.pop()
        return next
      })
      if (!res.ok || !res.transcript?.trim()) {
        const err = res.message || res.error || 'Não consegui transcrever o áudio.'
        setMessages((prev) => [...prev, { id: uid(), role: 'system', text: err }])
        return
      }
      await ask(res.transcript.trim())
    } catch (e) {
      setMessages((prev) => {
        const next = [...prev]
        const last = next[next.length - 1]
        if (last?.role === 'system' && last.text.startsWith('A transcrever')) next.pop()
        return next
      })
      const err =
        e instanceof Error && e.name === 'AbortError'
          ? 'Tempo esgotado na transcrição.'
          : e instanceof Error
            ? e.message
            : 'Falha ao enviar áudio.'
      setMessages((prev) => [...prev, { id: uid(), role: 'system', text: err }])
    } finally {
      setTranscribing(false)
    }
  }

  function resetRecUi() {
    setRecPhase('idle')
    setRecMs(0)
    setRecStream(null)
    recAccumRef.current = 0
    recBlobRef.current = null
    recMimeRef.current = 'audio/webm'
    previewAudioRef.current?.pause()
    previewAudioRef.current = null
    if (recUrlRef.current) {
      URL.revokeObjectURL(recUrlRef.current)
      recUrlRef.current = null
    }
  }

  function stopMic() {
    mediaStreamRef.current?.getTracks().forEach((t) => t.stop())
    mediaStreamRef.current = null
    mediaRecorderRef.current = null
    chunksRef.current = []
    setRecStream(null)
  }

  function discardRecording() {
    recIntentRef.current = 'discard'
    recAccumRef.current += Date.now() - recTickAtRef.current
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop()
      }
    } catch {
      /* ignore */
    }
    stopMic()
    resetRecUi()
  }

  function stopForReview() {
    const rec = mediaRecorderRef.current
    if (!rec || rec.state === 'inactive') return
    recIntentRef.current = 'review'
    recAccumRef.current += Date.now() - recTickAtRef.current
    setRecMs(recAccumRef.current)
    try {
      rec.stop()
    } catch {
      /* ignore */
    }
  }

  function playPreview() {
    const url = recUrlRef.current
    if (!url) return
    previewAudioRef.current?.pause()
    const audio = new Audio(url)
    previewAudioRef.current = audio
    audio.onended = () => setRecPhase('review')
    void audio.play()
    setRecPhase('playing')
  }

  function stopPreview() {
    previewAudioRef.current?.pause()
    previewAudioRef.current = null
    setRecPhase('review')
  }

  function sendRecording() {
    if (recPhase === 'recording') {
      const rec = mediaRecorderRef.current
      if (!rec || rec.state === 'inactive') return
      recIntentRef.current = 'send'
      try {
        rec.stop()
      } catch {
        /* ignore */
      }
      return
    }
    const blob = recBlobRef.current
    if (!blob) return
    const mime = recMimeRef.current
    resetRecUi()
    void handleAudioBlob(blob, mime)
  }

  function handleBack() {
    if (recOpen) {
      discardRecording()
      return
    }
    onClose()
  }

  async function startRecording() {
    if (voiceBusy) return
    if (!micSupported) {
      setMessages((prev) => [
        ...prev,
        {
          id: uid(),
          role: 'system',
          text: 'Este navegador não permite gravação de áudio. Usa Chrome/Edge ou o Telegram.',
        },
      ])
      return
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      mediaStreamRef.current = stream
      const mime = pickRecorderMime()
      const recorder = mime
        ? new MediaRecorder(stream, { mimeType: mime })
        : new MediaRecorder(stream)
      mediaRecorderRef.current = recorder
      chunksRef.current = []
      recIntentRef.current = 'send'
      recAccumRef.current = 0
      recTickAtRef.current = Date.now()
      recBlobRef.current = null
      recorder.ondataavailable = (ev) => {
        if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data)
      }
      recorder.onstop = () => {
        const intent = recIntentRef.current
        const usedMime = recorder.mimeType || mime || 'audio/webm'
        const blob = new Blob(chunksRef.current, { type: usedMime })
        mediaStreamRef.current?.getTracks().forEach((t) => t.stop())
        mediaStreamRef.current = null
        mediaRecorderRef.current = null
        chunksRef.current = []
        setRecStream(null)
        if (intent === 'discard') return
        if (blob.size < 800) {
          resetRecUi()
          setMessages((prev) => [
            ...prev,
            { id: uid(), role: 'system', text: 'Áudio demasiado curto — tenta de novo.' },
          ])
          return
        }
        recMimeRef.current = usedMime
        recBlobRef.current = blob
        if (intent === 'review') {
          if (recUrlRef.current) URL.revokeObjectURL(recUrlRef.current)
          recUrlRef.current = URL.createObjectURL(blob)
          setRecPhase('review')
          return
        }
        resetRecUi()
        void handleAudioBlob(blob, usedMime)
      }
      recorder.start(200)
      setRecStream(stream)
      setRecMs(0)
      setRecPhase('recording')
    } catch (e) {
      const name = e instanceof DOMException ? e.name : ''
      const inFrame = typeof window !== 'undefined' && window.self !== window.top
      const text =
        name === 'NotFoundError'
          ? 'Não encontrei microfone neste dispositivo.'
          : name === 'NotAllowedError' && inFrame
            ? 'O browser bloqueou o microfone no painel. Permite o acesso quando pedir e tenta outra vez.'
            : name === 'NotAllowedError'
              ? 'O browser bloqueou o microfone. No cadeado da barra de endereço, permite o microfone e tenta outra vez.'
              : 'Não consegui aceder ao microfone. Permite o acesso no navegador e tenta outra vez.'
      setMessages((prev) => [...prev, { id: uid(), role: 'system', text }])
    }
  }

  const canSend = Boolean(input.trim()) && !voiceBusy

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        position: 'relative',
        bgcolor: 'background.default',
        pb: 'env(safe-area-inset-bottom, 0px)',
      }}
    >
      {/* Quando expandido, o botão «reduzir» fica no BiLayout (paridade Financial:
          fixed na junção sidebar|chat, fora do stacking do modal). */}
      {onToggleExpand && !expanded ? (
        <IconButton
          aria-label="expandir chat"
          onClick={onToggleExpand}
          sx={{
            display: { xs: 'none', sm: 'inline-flex' },
            position: 'absolute',
            left: 0,
            top: '50%',
            zIndex: (t) => t.zIndex.modal + 2,
            width: 40,
            height: 48,
            transform: 'translate(-50%, -50%)',
            borderRadius: '18px 8px 8px 18px',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            boxShadow: '0 2px 10px rgba(15, 23, 42, 0.22)',
            '&:hover': { bgcolor: 'primary.dark' },
          }}
        >
          <Hicon name="chevron-left" sx={{ fontSize: 22 }} />
        </IconButton>
      ) : null}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          px: 1,
          py: 0.75,
          minWidth: 0,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <IconButton onClick={handleBack} aria-label={recOpen ? 'cancelar gravação' : 'fechar'} size="small">
          <Hicon name="chevron-left" sx={{ fontSize: 22 }} />
        </IconButton>
        <Avatar
          sx={{
            width: 40,
            height: 40,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            fontFamily: '"Outfit", sans-serif',
            fontWeight: 700,
            fontSize: 15,
          }}
        >
          RI
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="subtitle1"
            sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, lineHeight: 1.2 }}
            noWrap
          >
            Fiesta IA
          </Typography>
            <Select
            size="small"
            variant="standard"
            value={
              chatModels.some((m) => m.id === chatModel)
                ? chatModel
                : chatModels[0]?.id || DEFAULT_CHAT_MODEL
            }
            disabled={voiceBusy}
            aria-label="modelo"
            onChange={(e) => setChatModel(String(e.target.value))}
            disableUnderline
            sx={{
              mt: 0.15,
              minWidth: 0,
              maxWidth: '100%',
              fontSize: 12,
              color: 'text.secondary',
              '& .MuiSelect-select': {
                py: 0,
                pr: '22px !important',
              },
              '& .MuiSelect-icon': { color: 'text.secondary', fontSize: 18 },
            }}
          >
            {chatModels.map((m) => (
              <MenuItem key={m.id} value={m.id} sx={{ fontSize: 13 }}>
                {m.label}
                {m.hint ? ` · ${m.hint}` : ''}
              </MenuItem>
            ))}
          </Select>
        </Box>
        <IconButton
          aria-label="mais opções"
          size="small"
          onClick={(e) => setMenuEl(e.currentTarget)}
        >
          <MoreGlyph />
        </IconButton>
        <Menu
          anchorEl={menuEl}
          open={Boolean(menuEl)}
          onClose={() => setMenuEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem
            disabled={voiceBusy}
            onClick={() => {
              setMenuEl(null)
              setLlmSettingsOpen(true)
            }}
          >
            LLM da organização…
          </MenuItem>
          <MenuItem
            disabled={voiceBusy}
            onClick={() => {
              setMenuEl(null)
              clearChat()
            }}
          >
            Limpar conversa
          </MenuItem>
        </Menu>
      </Stack>

      <OrgLlmSettingsDialog
        open={llmSettingsOpen}
        onClose={() => setLlmSettingsOpen(false)}
      />

      <Box sx={{ flex: 1, minHeight: 0, position: 'relative', minWidth: 0 }}>
      <Box
        ref={listRef}
        onScroll={syncJumpVisibility}
        sx={{
          height: '100%',
          px: expanded ? { sm: '4%', md: '5%', lg: '6%' } : 3,
          py: 1.5,
          overflowY: 'auto',
          overflowX: 'hidden',
          minWidth: 0,
          ...LIST_SCROLL_SX,
        }}
      >
        <Stack spacing={0.75} sx={{ minWidth: 0 }}>
          {messages.map((m) => {
            const clock = messageClock(m.id, m.at)
            if (m.role === 'system') {
              return (
                <Box key={m.id} sx={{ alignSelf: 'center', maxWidth: '86%', px: 1, py: 0.5 }}>
                  <MessageBody text={m.text} muted />
                </Box>
              )
            }
            const mine = m.role === 'user'
            return (
              <Box
                key={m.id}
                sx={{
                  alignSelf: mine ? 'flex-end' : 'flex-start',
                  maxWidth: '82%',
                  minWidth: 0,
                  px: 1.5,
                  pt: 1,
                  pb: 0.5,
                  borderRadius: mine ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                  bgcolor: mine ? 'primary.main' : 'background.paper',
                  color: mine ? 'primary.contrastText' : 'text.primary',
                  boxShadow: mine ? 'none' : '0 1px 2px rgba(15, 23, 42, 0.06)',
                  overflow: 'hidden',
                }}
              >
                <MessageBody text={m.text} />
                {clock ? (
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      textAlign: 'right',
                      mt: 0.35,
                      fontSize: 11,
                      lineHeight: 1,
                      opacity: 0.65,
                    }}
                  >
                    {clock}
                  </Typography>
                ) : null}
              </Box>
            )
          })}
          {busy || transcribing ? (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 0.75, py: 0.5 }}>
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">
                {transcribing ? 'A transcrever áudio…' : 'A escrever…'}
              </Typography>
            </Stack>
          ) : null}
          <div ref={bottomRef} />
        </Stack>
      </Box>
      {showJump ? (
        <IconButton
          aria-label="ir para o fim"
          onClick={jumpToBottom}
          sx={{
            position: 'absolute',
            right: 12,
            bottom: 12,
            width: 48,
            height: 48,
            bgcolor: 'background.paper',
            color: 'text.secondary',
            boxShadow: '0 2px 8px rgba(15, 23, 42, 0.16)',
            '&:hover': { bgcolor: 'background.paper' },
          }}
        >
          <JumpDownGlyph />
        </IconButton>
      ) : null}
      </Box>

      <Box
        sx={{
          bgcolor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          px: expanded ? 1.25 : 3,
          py: 1,
          minWidth: 0,
          width: expanded ? '50%' : '100%',
          mx: 'auto',
        }}
      >
        {recOpen ? (
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.75}
            sx={{
              flex: 1,
              minWidth: 0,
              px: 1,
              py: 0.5,
              borderRadius: 999,
              bgcolor: 'background.default',
            }}
          >
            <IconButton
              aria-label="apagar gravação"
              size="small"
              onClick={discardRecording}
              sx={{ color: 'error.main' }}
            >
              <Hicon name="trash" sx={{ fontSize: 18 }} />
            </IconButton>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: recPhase === 'recording' ? 'error.main' : 'text.disabled',
                flexShrink: 0,
              }}
            />
            <RecWaveform stream={recStream} paused={recPhase !== 'recording'} />
            <Typography
              variant="caption"
              sx={{ fontVariantNumeric: 'tabular-nums', minWidth: 52, color: 'text.secondary' }}
            >
              {formatRecClock(recMs)}
            </Typography>
            <IconButton
              aria-label={
                recPhase === 'recording'
                  ? 'parar gravação'
                  : recPhase === 'playing'
                    ? 'parar reprodução'
                    : 'ouvir gravação'
              }
              size="small"
              onClick={() => {
                if (recPhase === 'recording') stopForReview()
                else if (recPhase === 'playing') stopPreview()
                else playPreview()
              }}
              sx={{ color: 'text.secondary' }}
            >
              {recPhase === 'review' ? <PlayGlyph /> : <StopGlyph />}
            </IconButton>
          </Stack>
        ) : (
          <TextField
            size="small"
            fullWidth
            multiline
            maxRows={4}
            placeholder="Mensagem"
            value={input}
            disabled={voiceBusy}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                void ask(input)
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 999,
                bgcolor: 'background.default',
              },
            }}
          />
        )}
        <IconButton
          aria-label={recOpen ? 'enviar áudio' : canSend ? 'enviar' : 'gravar áudio'}
          disabled={recOpen ? false : canSend ? voiceBusy : voiceBusy || !micSupported}
          onClick={() => {
            if (recOpen) sendRecording()
            else if (canSend) void ask(input)
            else void startRecording()
          }}
          sx={{
            width: 44,
            height: 44,
            flexShrink: 0,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            '&:hover': { bgcolor: 'primary.dark' },
            '&.Mui-disabled': {
              bgcolor: 'action.disabledBackground',
              color: 'action.disabled',
            },
          }}
        >
          {recOpen || canSend ? <SendGlyph /> : <MicGlyph />}
        </IconButton>
      </Stack>
      </Box>
    </Box>
  )
}
